import { PUBLIC_UNIFIED_ENDPOINT } from '$env/static/public';
import { GAME_OF_THE_DAY_RATING } from '$lib/constants';
import { GamesCategory, type GameCDS, type GamesCollection } from '$lib/types/games';
import Logger from '$lib/utils/logger';
import { fetchQuery } from '..';
import { getHeaders } from '../../headers';
import { get } from '../../requester';
import { getGamesCategoriesQuery } from '../queries/games';

const nostraGamesUrl = `${PUBLIC_UNIFIED_ENDPOINT}/swish/portal/nostra`;

const config = {
  headers: getHeaders()
};

/**
 * get all game categories
 * @returns {Promise<GameCategory[]>}
 */
export const getGamesCategories = async () => {
  try {
    const response = await fetchQuery(getGamesCategoriesQuery());
    return response?.data?.queryResult?.data?.swishGamesCategoriesCollection?.items.filter(
      (category) =>
        category.categoryName !== GamesCategory.CardAndSlot &&
        category.categoryName !== GamesCategory.Board &&
        category.categoryName !== GamesCategory.Adventure &&
        category.categoryName !== GamesCategory.Racing
    );
  } catch (error) {
    Logger.error('Error in getGamesCategories:', error);
    throw error;
  }
};

/**
 * get games by category
 * @param category string
 * @param skip number
 * @param limit number
 * @returns {Promise<GamesCollection>}
 */

export const getGamesByCategory = async (
  category: string,
  skip: number,
  limit: number
): Promise<GamesCollection> => {
  const endpoint = ` ${nostraGamesUrl}/gamesByCategory?categoryName=${category}&skip=${skip}&limit=${limit}`;
  return get(endpoint, config);
};

/**
 * get popular/recommeded games
 * @param skip number
 * @param limit number
 * @returns {Promise<GamesCollection>}
 */

export const getPopularGames = (skip: number, limit: number) =>
  getGamesByCategory('Recommended', skip, limit);

/**
 * get games feed
 * @param skip number
 * @param limit number
 * @returns {Promise<GamesCollection>}
 */
export const getGamesFeed = async (skip = 0, limit = 10): Promise<GamesCollection> => {
  const endpoint = ` ${nostraGamesUrl}/gamesFeed?skip=${skip}&limit=${limit}`;
  return get(endpoint, config);
};

/**
 * get random game
 * @returns {Promise<GameCDS>}
 */

export const getRandomGame = async () => {
  const SKIP = 0;
  const LIMIT = 100;
  // fetches All games;
  const data = await getGamesFeed(SKIP, LIMIT);
  if (!data) return;
  const { items } = data as GamesCollection;
  // filters games with rating greater than GAME_OF_THE_DAY_RATING
  const filteredItems = items.filter(
    (item: GameCDS) => Math.ceil(parseInt(item.rating)) > GAME_OF_THE_DAY_RATING
  );
  Logger.log('filteredItems', filteredItems);
  // randomly selects an index from the filtered games
  const randomIndex = Math.floor(Math.random() * filteredItems.length);
  // returns a random game from the filtered games
  return filteredItems[randomIndex];
};

/**
 * get game by id
 * @param id string
 * @returns {Promise<GamesCollection>}
 */
export const getGameById = async (id: string): Promise<GamesCollection> => {
  const endpoint = ` ${nostraGamesUrl}/gamesByIds?gameIds=${id}`;
  return get(endpoint, config);
};

/**
 * get recently played games
 * @param id string[]
 * @returns {Promise<GameCDS[]>}
 */

export const getRecentlyPlayedGamesById = async (
  recentlyPlayedGamesByIds: string[]
): Promise<GameCDS[]> => {
  const ids = recentlyPlayedGamesByIds.join(',');
  const endpoint = ` ${nostraGamesUrl}/gamesByIds?gameIds=${ids}`;
  return ((await get(endpoint, config)) as GamesCollection).items;
};
